import { useReactTable, getCoreRowModel, flexRender, type Column, type Row } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useRef, useEffect, type CSSProperties } from 'react';
import { ResizeHandler, SCROLL_CONTAINER_CLASSNAME, SortIndicator } from './components';
import {
  useColumnOrderState,
  useColumnPinningState,
  useOrderingState,
  useSizingState,
  useTableInfiniteQuery,
  useVisibilityState,
  useTableData,
  withActionsLast,
} from './utils';
import { cn } from '@nexelem/utils';
import { Skeleton } from '@nexelem/components';
import { TableEmptyState } from './TableEmptyState';
import { TableProvider } from './utils';
import { ChildrenDrawerPortals } from '@nexelem/drawer';
import { SpecialColumnsIds } from './SpecialColumns';

const LOADING_ROWS_COUNT = 5;

const COLUMN_RESIZE_ON_CHANGE = false;

type TableProps<T extends object> = {
  onRowClick?: (row: Row<T>) => void;
  /** Compared with `row.original.id` when present */
  activeRowId?: string | number;
  noResultsText?: string;
  className?: string;
};

export const Table = <T extends object>({ onRowClick, activeRowId, noResultsText, className }: TableProps<T>) => {
  const { columns } = useTableData();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [sorting, onSortingChange] = useOrderingState();
  const [columnSizing, onColumnSizingChange] = useSizingState();
  const [columnOrder, onColumnOrderChange] = useColumnOrderState();
  const [columnVisibility, onColumnVisibilityChange] = useVisibilityState();
  const [columnPinning, onColumnPinningChange] = useColumnPinningState();

  const { showSkeleton, data, dataLength, fetchMoreOnBottomReached } = useTableInfiniteQuery();

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange,
    onColumnSizingChange,
    onColumnVisibilityChange,
    onColumnOrderChange,
    onColumnPinningChange,
    state: {
      sorting,
      columnSizing,
      columnVisibility,
      columnOrder,
      columnPinning,
    },
    columnResizeMode: COLUMN_RESIZE_ON_CHANGE ? 'onChange' : 'onEnd',
  });

  const { rows } = table.getRowModel();

  const rowVirtualizer = useVirtualizer({
    count: dataLength + (showSkeleton ? LOADING_ROWS_COUNT : 0),
    estimateSize: () => 53,
    getScrollElement: () => tableContainerRef.current,
    overscan: 5,
  });

  const isEmpty = rowVirtualizer.getVirtualItems().length === 0;

  useEffect(() => {
    fetchMoreOnBottomReached(tableContainerRef.current);
  }, [fetchMoreOnBottomReached]);

  return (
    <TableProvider table={table}>
      <div
        className={cn(
          'relative h-full',
          isEmpty ? 'overflow-hidden' : 'overflow-auto',
          SCROLL_CONTAINER_CLASSNAME,
          className,
        )}
        ref={tableContainerRef}
        onScroll={(e) => fetchMoreOnBottomReached(e.currentTarget)}
      >
        {isEmpty && <TableEmptyState noResultsText={noResultsText} />}
        <table
          className="grid w-full min-w-full"
          style={{ width: isEmpty ? '100%' : table.getTotalSize() > 0 ? `${table.getTotalSize()}px` : '100%' }}
        >
          <thead className="sticky top-0 z-10 bg-surface-primary">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr className="flex w-full border-b border-border-secondary" key={headerGroup.id}>
                {withActionsLast(headerGroup.headers, (header) => header.column.id).map((header) => {
                  const { style, className } = getCommonPinningStyles(header.column, true);

                  return (
                    <th
                      key={header.id}
                      style={style}
                      className={cn(
                        'relative m-0 flex h-11 items-center bg-surface-primary pl-3 text-start text-t14-sb text-text-secondary first:pl-6',
                        header.column.getCanResize() ? 'pr-4' : 'pr-3 last:pr-6',
                        className,
                      )}
                    >
                      <div
                        className={cn(
                          'flex w-full min-w-0',
                          header.column.getCanSort() && 'group cursor-pointer hover:text-text-brand',
                          header.column.getIsSorted() && 'text-text-brand',
                          header.column.columnDef.meta?.textAlign === 'right' && 'flex-row-reverse justify-start gap-2',
                        )}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span className="truncate">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </span>
                        {header.column.getCanSort() && <SortIndicator order={header.column.getIsSorted()} />}
                      </div>
                      {header.column.getCanResize() && (
                        <ResizeHandler
                          style={{
                            transform:
                              header.column.getIsResizing() && !COLUMN_RESIZE_ON_CHANGE
                                ? `translateX(${table.getState().columnSizingInfo.deltaOffset}px)`
                                : '',
                          }}
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="relative grid" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index];
              const isActive = row?.original?.id && activeRowId === row.original.id;

              return (
                <tr
                  key={virtualRow.key}
                  className={cn(
                    'group absolute left-0 flex h-13 w-full border-border-secondary bg-surface-primary not-last:border-b hover:bg-surface-hover-lighter',
                    onRowClick && row && 'cursor-pointer',
                    isActive && 'bg-surface-focus-lighter',
                  )}
                  style={{
                    top: `${virtualRow.start}px`,
                  }}
                  onClick={row && onRowClick ? () => onRowClick(row) : undefined}
                >
                  {row
                    ? withActionsLast(row.getVisibleCells(), (cell) => cell.column.id).map((cell) => {
                        const canResize = cell.column.getCanResize();
                        const isRightAligned = cell.column.columnDef.meta?.textAlign === 'right';
                        const { style, className } = getCommonPinningStyles(cell.column, false, isActive);

                        return (
                          <td
                            key={cell.id}
                            data-field={cell.column.id}
                            style={style}
                            className={cn(
                              'm-0 flex items-center overflow-hidden pl-3 text-s16 first:pl-6',
                              canResize ? 'pr-4' : 'pr-3 last:pr-6',
                              isRightAligned && 'justify-end',
                              className,
                            )}
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        );
                      })
                    : withActionsLast(table.getVisibleFlatColumns(), (column) => column.id).map((column) => {
                        const canResize = column.getCanResize();
                        const isRightAligned = column.columnDef.meta?.textAlign === 'right';
                        const { style, className } = getCommonPinningStyles(column, false, isActive);
                        return (
                          <td
                            key={column.id}
                            data-field={column.id}
                            style={style}
                            className={cn(
                              'm-0 flex items-center overflow-hidden pl-3 text-s16 first:pl-6',
                              column.getSize() < 40 && 'invisible',
                              canResize ? 'pr-4' : 'pr-3 last:pr-6',
                              isRightAligned && 'justify-end',
                              className,
                            )}
                          >
                            <Skeleton style={{ width: Math.max(0, column.getSize() - 16) }} />
                          </td>
                        );
                      })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <ChildrenDrawerPortals />
    </TableProvider>
  );
};

const getCommonPinningStyles = (column: Column<unknown>, isHeader = false, isActive = false) => {
  const isActions = column.id === SpecialColumnsIds.ACTIONS;
  const pinning = column.getIsPinned() || (isActions && 'right');
  const isPinned = Boolean(pinning);
  const isLastLeftPinnedColumn = pinning === 'left' && column.getIsLastColumn('left');
  const size = column.getSize();
  // Actions is not in columnPinning state — always stick to the far right edge of the scrollport.
  const rightOffset = isActions ? 0 : pinning === 'right' ? column.getAfter('right') : undefined;

  return {
    className: cn(
      'box-border',
      isLastLeftPinnedColumn && !isHeader && 'border-r border-border-primary',
      isPinned
        ? cn(
            'sticky group-hover:bg-surface-hover-lighter',
            isHeader ? 'bg-surface-primary' : isActive ? 'bg-surface-focus-lighter' : 'bg-surface-primary',
          )
        : 'relative bg-transparent',
    ),
    style: {
      left: pinning === 'left' ? `${column.getStart('left')}px` : undefined,
      right: rightOffset !== undefined ? `${rightOffset}px` : undefined,
      // Push Actions to the row's right edge when the row is wider than the columns.
      marginLeft: isActions ? 'auto' : undefined,
      position: isPinned ? 'sticky' : 'relative',
      width: `${size}px`,
      minWidth: `${size}px`,
      maxWidth: `${size}px`,
      flex: `0 0 ${size}px`,
      zIndex: isPinned ? (isHeader ? 2 : 1) : isHeader ? 1 : 0,
    } satisfies CSSProperties,
  };
};
